import * as React from 'react'
import { Input, Label } from '@/shared/ui'
import { CountrySelect } from './CountrySelect'
import { COUNTRY_BY_CODE } from '@/shared/data/countries'

export type PhoneInputIntlProps = {
  label?: string
  value: string // E.164 (+DDI + número) ou vazio
  onChange: (e164: string) => void
  defaultCountry?: string // ISO2, default 'BR'
  placeholderBR?: string
  required?: boolean
}

function onlyDigits(s: string): string { return (s || '').replace(/\D/g, '') }

function formatBR(national: string): string {
  const d = onlyDigits(national).slice(0, 11)
  if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3').trim()
  return d.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3').trim()
}

function formatIntlGeneric(national: string): string {
  // Formatação leve: agrupa a cada 3-4 dígitos
  const d = onlyDigits(national).slice(0, 15)
  return d.replace(/(\d{3})(?=\d)/g, '$1 ').trim()
}

export function PhoneInputIntl({ label = 'Telefone', value, onChange, defaultCountry = 'BR', placeholderBR = '(11) 98888-7777', required }: PhoneInputIntlProps) {
  const [country, setCountry] = React.useState<string>(defaultCountry)
  const [national, setNational] = React.useState<string>('')
  const [touched, setTouched] = React.useState(false)

  // Deriva país e nacional a partir de value (E.164)
  React.useEffect(() => {
    const v = (value || '').trim()
    const hasPlus = v.startsWith('+')
    if (!v) {
      setCountry(defaultCountry)
      setNational('')
      return
    }
    if (!hasPlus) {
      setCountry(defaultCountry)
      setNational(v)
      return
    }
    const digits = v.replace(/\D/g, '')
    const candidates = Array.from(COUNTRY_BY_CODE.values()).map(c => c.dial).sort((a, b) => b.length - a.length)
    for (const dial of candidates) {
      if (digits.startsWith(dial)) {
        const code = Array.from(COUNTRY_BY_CODE.entries()).find(([k, c]) => c.dial === dial)?.[0]
        if (code) {
          setCountry(code)
          setNational(digits.slice(dial.length))
          return
        }
      }
    }
    setCountry(defaultCountry)
    setNational(digits)
  }, [value, defaultCountry])

  function emit(nextCountry: string, nextNationalDisplay: string) {
    const c = COUNTRY_BY_CODE.get(nextCountry)
    const natDigits = onlyDigits(nextNationalDisplay)
    const e164 = c ? `+${c.dial}${natDigits}` : natDigits
    onChange(e164)
  }

  const isBR = country === 'BR'
  const formatted = isBR ? formatBR(national) : formatIntlGeneric(national)
  const placeholder = isBR ? placeholderBR : 'número'

  const natDigits = onlyDigits(national)
  function isValidPhone(): boolean {
    if (!natDigits) return !required
    if (country === 'BR') return natDigits.length === 10 || natDigits.length === 11
    if (country === 'US' || country === 'CA') return natDigits.length === 10
    return natDigits.length >= 7 && natDigits.length <= 15
  }
  const invalid = touched && !isValidPhone()

  return (
    <div className="space-y-1.5">
      {label && <Label>{label}</Label>}
      <div className="flex gap-2">
        <div className="w-40 min-w-[140px]">
          <CountrySelect className="h-12" value={country} onChange={(code) => { setCountry(code); emit(code, formatted) }} />
        </div>
        <Input
          value={formatted}
          onChange={(e) => { setNational(e.target.value); emit(country, e.target.value) }}
          onBlur={() => setTouched(true)}
          onKeyDown={(e: any) => {
            if (e.ctrlKey || e.metaKey) return
            const allowed = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Home','End']
            if (allowed.includes(e.key)) return
            if (/^\d$/.test(e.key)) return
            e.preventDefault()
          }}
          placeholder={placeholder}
          required={required}
          aria-invalid={invalid}
          className={invalid ? 'ring-2 ring-red-500' : undefined as any}
        />
      </div>
      {invalid && (
        <div className="text-xs text-destructive">Número de telefone inválido</div>
      )}
    </div>
  )
}

export default PhoneInputIntl

